// Modal for picking a screen's offline-alert threshold. Preset pills only —
// a "build a custom rule" number-input used to sit alongside them, but a
// free-form hours/days-per-week builder was more friction than the actual
// range of values operators picked justified. Replaced with a wider spread
// of discrete presets (hours through weeks) that covers the same range
// without a second input mode. Both encodings collapse to an integer
// "minutes" value the backend stores on Screen.offline_alert_minutes.
//
// Sweep job side: backend/app/main.py:_offline_alert_sweep_loop reads
// offline_alert_minutes and raises a ScreenLog when crossed. UI surface
// (bell icon / panel) comes in a follow-up.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Bell, BellOff } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'

interface Props {
  open:    boolean
  value:   number | null              // current minutes; null = never
  onClose: () => void
  onSave:  (minutes: number | null) => void
}

interface Preset {
  label: string
  minutes: number | null
}

// Built-in pills — spans the same hours..weeks range the old custom builder
// covered (1h..23h daily, 1..30 days weekly), just as discrete stops instead
// of a free-form number input.
function presets(t: (k: string, fb: string, opts?: Record<string, unknown>) => string): Preset[] {
  return [
    { label: t('common.never',        'Never'),     minutes: null   },
    { label: t('offlineAlert.1h',     '1 hour'),    minutes: 60     },
    { label: t('offlineAlert.2h',     '2 hours'),   minutes: 120    },
    { label: t('offlineAlert.4h',     '4 hours'),   minutes: 240    },
    { label: t('offlineAlert.6h',     '6 hours'),   minutes: 360    },
    { label: t('offlineAlert.12h',    '12 hours'),  minutes: 720    },
    { label: t('offlineAlert.1d',     '1 day'),     minutes: 1440   },
    { label: t('offlineAlert.2d',     '2 days'),    minutes: 2880   },
    { label: t('offlineAlert.3d',     '3 days'),    minutes: 4320   },
    { label: t('offlineAlert.1w',     '1 week'),    minutes: 10080  },
    { label: t('offlineAlert.2w',     '2 weeks'),   minutes: 20160  },
  ]
}

export default function OfflineAlertModal({ open, value, onClose, onSave }: Props) {
  const { t } = useTranslation()
  const list = presets(t)

  // Local draft so the parent doesn't flicker while the operator is picking.
  const [draft, setDraft] = useState<number | null>(value)

  // Re-seed when the modal opens with a new screen's value.
  useEffect(() => {
    if (!open) return
    setDraft(value)
  }, [open, value])

  const isPresetActive = (p: Preset) => p.minutes === draft

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            {t('offlineAlert.title', 'Offline alert')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'offlineAlert.description',
              'How long should this screen be offline before we raise an alert in the dashboard?',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <div className="flex flex-wrap gap-2">
            {list.map(p => (
              <button
                key={String(p.minutes)}
                type="button"
                onClick={() => setDraft(p.minutes)}
                className={[
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors',
                  isPresetActive(p)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-foreground border-border hover:border-muted-foreground/40',
                ].join(' ')}
              >
                {p.minutes === null && <BellOff className="w-3.5 h-3.5" />}
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => { onSave(draft); onClose() }}>
            {t('common.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Plain-English label for the offline-alert button on ScreenDetailsPage.
// Handles arbitrary minute values (not just the current preset set) since
// screens configured before this change may still carry an old custom value.
export function describeAlertRule(
  minutes: number,
  t: TFunction,
): string {
  if (minutes >= 1440 && minutes % 1440 === 0) {
    const d = minutes / 1440
    return d === 1
      ? t('offlineAlert.afterOneDay',  'After 1 day')
      : t('offlineAlert.afterNDays',   { count: d, defaultValue: 'After {{count}} days' })
  }
  if (minutes % 60 === 0) {
    const h = minutes / 60
    return h === 1
      ? t('offlineAlert.afterOneHour', 'After 1 hour')
      : t('offlineAlert.afterNHours',  { count: h, defaultValue: 'After {{count}} hours' })
  }
  return t('offlineAlert.afterNMin', { count: minutes, defaultValue: 'After {{count}} minutes' })
}
